import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '@/components/Button.tsx';
import { MessageIcon } from '@/components/icons.tsx';
import type { SupportTicketCategory } from '@dermalink/shared';
import styles from './SupportTicketFormDialog.module.css';

const SUBJECT_MAX = 200;
const BODY_MAX = 4000;

interface SupportTicketFormDialogProps {
  open: boolean;
  submitting?: boolean;
  serverError?: string | null;
  onSubmit: (category: SupportTicketCategory, subject: string, body: string) => void;
  onClose: () => void;
}

export function SupportTicketFormDialog({
  open,
  submitting = false,
  serverError,
  onSubmit,
  onClose,
}: SupportTicketFormDialogProps) {
  const headingId = useId();
  const [category, setCategory] = useState<SupportTicketCategory>('feedback');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const firstRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (!open) return;
    setCategory('feedback');
    setSubject('');
    setBody('');
    setSubmitted(false);
    queueMicrotask(() => firstRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, submitting, onClose]);

  if (!open) return null;

  const subjectError = subject.trim().length === 0 ? 'Onderwerp is verplicht.' : subject.length > SUBJECT_MAX ? `Max. ${SUBJECT_MAX} tekens.` : null;
  const bodyError = body.trim().length === 0 ? 'Beschrijving is verplicht.' : body.length > BODY_MAX ? `Max. ${BODY_MAX} tekens.` : null;
  const hasClientError = !!(subjectError || bodyError);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (hasClientError || submitting) return;
    onSubmit(category, subject.trim(), body.trim());
  }

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-labelledby={headingId}>
      <div className={styles.panel}>
        <div className="icon-wrap icon-wrap-primary"><MessageIcon size={24} /></div>
        <h2 id={headingId} className={styles.title}>Nieuw ticket</h2>
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.field}>
            <label className="field-label" htmlFor="ticket-category">Type</label>
            <select ref={firstRef} id="ticket-category" className="field-input" value={category} onChange={(e) => setCategory(e.target.value as SupportTicketCategory)} disabled={submitting}>
              <option value="feedback">Feedback</option>
              <option value="support">Ondersteuning</option>
            </select>
          </div>
          <div className={styles.field}>
            <label className="field-label" htmlFor="ticket-subject">Onderwerp</label>
            <input id="ticket-subject" className={submitted && subjectError ? 'field-input field-input-error' : 'field-input'} type="text" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={SUBJECT_MAX} autoComplete="off" disabled={submitting} />
            <span className={styles.fieldError}>{(submitted && subjectError) || ' '}</span>
          </div>
          <div className={styles.field}>
            <label className="field-label" htmlFor="ticket-body">Beschrijving</label>
            <textarea id="ticket-body" className={submitted && bodyError ? 'field-input field-input-error' : 'field-input'} rows={6} value={body} onChange={(e) => setBody(e.target.value)} maxLength={BODY_MAX} disabled={submitting} />
            <span className={styles.fieldError}>{(submitted && bodyError) || ' '}</span>
          </div>
          {serverError && <p className={styles.serverError} role="alert">{serverError}</p>}
          <div className={styles.actions}>
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Annuleren</Button>
            <Button type="submit" variant="primary" disabled={submitting}>{submitting ? 'Bezig…' : 'Versturen'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
